import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getExpense, createExpense, updateExpense, getExpenseCategories } from '../services/api'
import { toInputDate } from '../lib/formatters'

export default function ExpenseForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const [form, setForm] = useState({
    expenseDate: toInputDate(new Date()), categoryId: '', description: '',
    amount: '', vendor: '', reference: '', notes: '',
  })
  const [categories, setCategories] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    getExpenseCategories().then((res) => setCategories(res.data)).catch(() => {})
    if (isEdit) {
      getExpense(id).then((res) => {
        const e = res.data
        setForm({
          expenseDate: toInputDate(e.expenseDate), categoryId: e.categoryId || '',
          description: e.description, amount: e.amount?.toString() || '', vendor: e.vendor || '',
          reference: e.reference || '', notes: e.notes || '',
        })
      }).catch(() => navigate('/expenses'))
    }
  }, [id, isEdit, navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSaving(true)
    try {
      const payload = { ...form, amount: Number(form.amount) }
      if (isEdit) await updateExpense(id, payload)
      else await createExpense(payload)
      navigate('/expenses')
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value })

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-semibold text-gray-900">{isEdit ? 'Edit Expense' : 'New Expense'}</h1>

      <form onSubmit={handleSubmit} className="card space-y-4">
        {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Date *</label>
            <input type="date" className="input" value={form.expenseDate} onChange={set('expenseDate')} onKeyDown={(e) => e.preventDefault()} required />
          </div>
          <div>
            <label className="label">Category *</label>
            <select className="select" value={form.categoryId} onChange={set('categoryId')} required>
              <option value="">Select category</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Amount (INR) *</label>
            <input type="number" min="0" step="0.01" className="input" value={form.amount} onChange={set('amount')} required />
          </div>
          <div>
            <label className="label">Vendor</label>
            <input className="input" value={form.vendor} onChange={set('vendor')} />
          </div>
          <div>
            <label className="label">Reference / Bill No.</label>
            <input className="input" value={form.reference} onChange={set('reference')} />
          </div>
        </div>
        <div>
          <label className="label">Description *</label>
          <textarea className="input" rows={2} value={form.description} onChange={set('description')} required />
        </div>
        <div>
          <label className="label">Notes</label>
          <textarea className="input" rows={2} value={form.notes} onChange={set('notes')} />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={() => navigate(-1)} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary">{saving ? 'Saving...' : isEdit ? 'Update' : 'Submit'}</button>
        </div>
      </form>
    </div>
  )
}
